// ============================================
// USER HEADER - Mobil Uyumlu
// ============================================

import { ChevronDown, LogOut, Settings, User, Briefcase, Menu } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useAuthStore } from '@/store/auth';
import { useLogout } from '@/hooks';
import { getInitials } from '@/utils';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { NotificationDropdown } from '@/components';
import { LanguageSwitcher, useTranslation } from '@crew-at-sea/i18n';

interface UserHeaderProps {
  onMenuClick: () => void;
}

export function UserHeader({ onMenuClick }: UserHeaderProps) {
  const { t } = useTranslation('common');
  const { user } = useAuthStore();
  const logoutMutation = useLogout();

  const fullName = user?.seafarer
    ? `${user.seafarer.firstName} ${user.seafarer.lastName}`
    : user?.email || t('user');
  const initials = getInitials(fullName);
  const avatarUrl = user?.seafarer?.avatarUrl;

  return (
    <header className="sticky top-0 z-40 h-14 md:h-16 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between h-full px-3 md:px-6">
        {/* Sol - Hamburger (mobil) + Boşluk (desktop) */}
        <div className="flex items-center gap-3">
          {/* Hamburger - sadece mobilde */}
          <button
            onClick={onMenuClick}
            className="p-2 -ml-1 rounded-maritime text-gray-600 hover:bg-gray-100 md:hidden"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Masaüstü boşluk (arama alanı eklenebilir) */}
          <div className="hidden md:block flex-1 max-w-md" />
        </div>

        {/* Sağ */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* Dil Değiştirme */}
          <LanguageSwitcher />

          {/* Bildirimler */}
          <NotificationDropdown />

          {/* Kullanıcı Menüsü */}
          <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger asChild>
              <button className="flex items-center gap-2 md:gap-3 p-1 md:p-1.5 rounded-maritime hover:bg-gray-100 transition-colors">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={fullName}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-maritime-100 text-maritime-700 font-medium text-sm">
                    {initials}
                  </div>
                )}
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-900">{fullName}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <ChevronDown className="hidden md:block w-4 h-4 text-gray-400" />
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="min-w-[200px] bg-white rounded-maritime shadow-lg border border-gray-200 py-1 z-50"
                sideOffset={5}
                align="end"
              >
                {/* Mobilde isim göster (header'da gizli) */}
                <DropdownMenu.Label className="px-3 py-2 border-b border-gray-100 md:hidden">
                  <p className="text-sm font-medium text-gray-900">{fullName}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </DropdownMenu.Label>

                <DropdownMenu.Label className="px-3 py-2 text-xs font-medium text-gray-500 uppercase">
                  {t('account')}
                </DropdownMenu.Label>

                <DropdownMenu.Item asChild>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer outline-none"
                  >
                    <User className="w-4 h-4" />
                    {t('myProfile')}
                  </Link>
                </DropdownMenu.Item>

                <DropdownMenu.Item asChild>
                  <Link
                    to="/applications"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer outline-none"
                  >
                    <Briefcase className="w-4 h-4" />
                    {t('myApplications')}
                  </Link>
                </DropdownMenu.Item>

                <DropdownMenu.Item asChild>
                  <Link
                    to="/settings"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer outline-none"
                  >
                    <Settings className="w-4 h-4" />
                    {t('settings')}
                  </Link>
                </DropdownMenu.Item>

                <DropdownMenu.Separator className="my-1 border-t border-gray-200" />

                <DropdownMenu.Item
                  className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer outline-none"
                  onSelect={() => logoutMutation.mutate()}
                >
                  <LogOut className="w-4 h-4" />
                  {t('logout')}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
    </header>
  );
}
